import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Download, Calendar, MapPin, Users, LogOut, Clock, CheckCircle, Loader, User, Edit, Upload, FileCheck, CreditCard, Cake, Heart, X, Eye, Lock, Mail, Phone, EyeOff, Save, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ClientDashboard = () => {
  const [itineraries, setItineraries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('itineraries');
  const [clientData, setClientData] = useState(null);
  const [profile, setProfile] = useState({
    birthday: '',
    anniversary: '',
    aadhar_number: '',
    pan_number: '',
    passport_number: '',
    passport_expiry: ''
  });
  const [accountInfo, setAccountInfo] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: ''
  });
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [editingProfile, setEditingProfile] = useState(false);
  const [editingAccount, setEditingAccount] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingAccount, setSavingAccount] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [accountError, setAccountError] = useState('');
  const [accountSuccess, setAccountSuccess] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [uploadMessage, setUploadMessage] = useState('');
  const [uploadError, setUploadError] = useState('');
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const clientToken = localStorage.getItem('client_token');
    const adminToken = localStorage.getItem('admin_token');
    const token = clientToken || adminToken;
    
    if (!token) {
      navigate('/client/login');
      return;
    }

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      
      if (adminToken && !clientToken) {
        if (payload.role !== 'client' && payload.role !== 'partner') {
          navigate('/admin/dashboard');
          return;
        }
      }
      
      const storedClientData = localStorage.getItem('client_data');
      if (storedClientData) {
        const parsedData = JSON.parse(storedClientData);
        setClientData(parsedData);
        setAccountInfo({
          first_name: parsedData.first_name || '',
          last_name: parsedData.last_name || '',
          email: parsedData.email || '',
          phone: parsedData.phone || ''
        });
      } else {
        setClientData({ email: payload.sub, name: payload.sub });
      }
    } catch (e) {
      navigate('/client/login');
      return;
    }

    fetchItineraries();
    fetchProfile();
  }, [navigate]);

  const getToken = () => {
    return localStorage.getItem('client_token') || localStorage.getItem('admin_token');
  };

  const fetchItineraries = async () => {
    try {
      const token = getToken();
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/client/my-itineraries`, { headers });
      setItineraries(response.data || []);
    } catch (error) {
      console.error('Error fetching itineraries:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('client_token');
        localStorage.removeItem('admin_token');
        navigate('/client/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchProfile = async () => {
    try {
      const token = getToken();
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/client/profile`, { headers });
      if (response.data) {
        setAccountInfo({
          first_name: response.data.first_name || '',
          last_name: response.data.last_name || '',
          email: response.data.email || '',
          phone: response.data.phone || ''
        });
        if (response.data.profile) {
          setProfile({
            birthday: response.data.profile.birthday || '',
            anniversary: response.data.profile.anniversary || '',
            aadhar_number: response.data.profile.aadhar_number || '',
            pan_number: response.data.profile.pan_number || '',
            passport_number: response.data.profile.passport_number || '',
            passport_expiry: response.data.profile.passport_expiry || ''
          });
          setDocuments(response.data.profile.documents || []);
        }
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  const handleFileUpload = async (e, docType) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      setUploadError('Please upload a JPG, PNG, or PDF file');
      setTimeout(() => setUploadError(''), 3000);
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setUploadError('File size must be less than 5MB');
      setTimeout(() => setUploadError(''), 3000);
      return;
    }

    setUploadingDoc(true);
    setUploadMessage('');
    setUploadError('');
    try {
      const token = getToken();
      const formData = new FormData();
      formData.append('file', file);
      formData.append('doc_type', docType);

      const response = await axios.post(`${API}/client/upload-document`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        setDocuments(prev => [...prev, response.data.document]);
        setUploadMessage(`"${file.name}" uploaded successfully as ${docType}`);
        setTimeout(() => setUploadMessage(''), 4000);
      }
    } catch (error) {
      console.error('Error uploading document:', error);
      setUploadError(error.response?.data?.detail || 'Failed to upload document');
      setTimeout(() => setUploadError(''), 4000);
    } finally {
      setUploadingDoc(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const deleteDocument = async (docId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;

    try {
      const token = getToken();
      await axios.delete(`${API}/client/document/${docId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDocuments(prev => prev.filter(d => d.id !== docId));
      alert('Document deleted successfully');
    } catch (error) {
      console.error('Error deleting document:', error);
      alert('Failed to delete document');
    }
  };

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      const token = getToken();
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/client/profile`, { profile }, { headers });
      setEditingProfile(false);
      alert('Profile updated successfully!');
    } catch (error) {
      console.error('Error saving profile:', error);
      alert('Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const saveAccountInfo = async () => {
    setSavingAccount(true);
    setAccountError('');
    setAccountSuccess('');
    try {
      const token = getToken();
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.put(`${API}/client/profile`, {
        first_name: accountInfo.first_name,
        last_name: accountInfo.last_name,
        phone: accountInfo.phone,
        email: accountInfo.email
      }, { headers });

      if (response.data.success) {
        const updatedClient = response.data.client;
        setClientData(prev => ({ ...prev, ...updatedClient }));
        localStorage.setItem('client_data', JSON.stringify({ ...clientData, ...updatedClient }));
        setEditingAccount(false);
        setAccountSuccess('Account info updated successfully!');
        setTimeout(() => setAccountSuccess(''), 3000);
      }
    } catch (error) {
      setAccountError(error.response?.data?.detail || 'Failed to update account info');
    } finally {
      setSavingAccount(false);
    }
  };

  const savePassword = async () => {
    setPasswordError('');
    setPasswordSuccess('');

    if (passwordData.new_password !== passwordData.confirm_password) {
      setPasswordError('New passwords do not match');
      return;
    }
    if (passwordData.new_password.length < 8) {
      setPasswordError('New password must be at least 8 characters');
      return;
    }

    setSavingPassword(true);
    try {
      const token = getToken();
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.put(`${API}/client/profile`, {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      }, { headers });

      if (response.data.success) {
        setPasswordSuccess('Password changed successfully!');
        setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
        setChangingPassword(false);
        setTimeout(() => setPasswordSuccess(''), 3000);
      }
    } catch (error) {
      setPasswordError(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setSavingPassword(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('client_token');
    localStorage.removeItem('admin_token');
    localStorage.removeItem('client_data');
    navigate('/client/login');
  };

  const getStatusBadge = (status, hasItinerary) => {
    if (hasItinerary) {
      return (
        <span className="status-badge bg-green-100 text-green-800">
          <CheckCircle size={14} />
          Ready
        </span>
      );
    }
    
    const statusConfig = {
      'completed': { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Completed' },
      'processing': { color: 'bg-blue-100 text-blue-800', icon: Loader, label: 'Processing' },
      'pending': { color: 'bg-yellow-100 text-yellow-800', icon: Clock, label: 'Pending' },
      'new': { color: 'bg-blue-100 text-blue-800', icon: Clock, label: 'New' }
    };
    
    const config = statusConfig[status] || statusConfig['pending'];
    const Icon = config.icon;
    
    return (
      <span className={`status-badge ${config.color}`}>
        <Icon size={14} />
        {config.label}
      </span>
    );
  };

  const displayName = clientData?.first_name || clientData?.name || clientData?.email || '';

  if (loading) {
    return (
      <div className="client-dashboard-loading">
        <div className="loading-spinner"></div>
        <p>Loading your journeys...</p>
      </div>
    );
  }

  return (
    <div className="client-dashboard" data-testid="client-dashboard">
      {/* Header */}
      <header className="client-header">
        <div className="client-header-content">
          <div className="client-welcome">
            <img 
              src="https://customer-assets.emergentagent.com/job_071d5548-d385-4daa-a48c-997f978ccc70/artifacts/mlh0ioi5_JNT%20LOGO%20Only%20Round.png" 
              alt="JNT" 
              className="client-logo"
            />
            <div>
              <h1 data-testid="welcome-heading">Welcome, {displayName}!</h1>
              <p>{clientData?.email}</p>
            </div>
          </div>
          <Button onClick={handleLogout} className="logout-btn" data-testid="client-logout">
            <LogOut size={18} />
            Logout
          </Button>
        </div>
      </header>

      {/* Tab Navigation */}
      <nav className="client-tabs">
        <button 
          className={`client-tab ${activeTab === 'itineraries' ? 'active' : ''}`}
          onClick={() => setActiveTab('itineraries')}
          data-testid="tab-itineraries"
        >
          <FileText size={18} />
          My Itineraries
        </button>
        <button 
          className={`client-tab ${activeTab === 'profile' ? 'active' : ''}`}
          onClick={() => setActiveTab('profile')}
          data-testid="tab-profile"
        >
          <User size={18} />
          Profile & Documents
        </button>
      </nav>

      {/* Main Content */}
      <main className="client-main">
        <div className="client-container">
          
          {/* Itineraries Tab */}
          {activeTab === 'itineraries' && (
            <>
              <div className="client-section-header">
                <h2><FileText size={24} /> Your Itineraries</h2>
                <p>View and download your personalized travel plans</p>
              </div>

              {itineraries.length === 0 ? (
                <div className="client-empty-state">
                  <MapPin size={64} />
                  <h3>No Itineraries Yet</h3>
                  <p>You haven't requested any travel itineraries yet.</p>
                  <Button onClick={() => navigate('/design-journey')} className="cta-btn" data-testid="request-itinerary">
                    Design Your Journey
                  </Button>
                </div>
              ) : (
                <div className="itineraries-grid">
                  {itineraries.map((itinerary) => (
                    <div key={itinerary.id} className="itinerary-card" data-testid={`itinerary-${itinerary.id}`}>
                      <div className="itinerary-card-header">
                        <h3>{itinerary.destination || 'Your Journey'}</h3>
                        {getStatusBadge(itinerary.status, itinerary.itinerary_generated)}
                      </div>
                      
                      <div className="itinerary-details">
                        <div className="itinerary-detail">
                          <MapPin size={16} />
                          <span>{itinerary.destination || 'Custom Destination'}</span>
                        </div>
                        <div className="itinerary-detail">
                          <Calendar size={16} />
                          <span>
                            {itinerary.travel_start_date && itinerary.travel_end_date 
                              ? `${itinerary.travel_start_date} - ${itinerary.travel_end_date}`
                              : 'Dates TBD'}
                          </span>
                        </div>
                        <div className="itinerary-detail">
                          <Users size={16} />
                          <span>{itinerary.num_travelers || 0} Travelers</span>
                        </div>
                      </div>

                      <div className="itinerary-card-actions">
                        {itinerary.itinerary_generated ? (
                          <a 
                            href={`${BACKEND_URL}/api/itinerary/download/${itinerary.request_id || itinerary.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="download-btn pdf-btn"
                            data-testid={`download-pdf-${itinerary.id}`}
                          >
                            <Download size={16} />
                            Download PDF
                          </a>
                        ) : (
                          <span className="pdf-pending">
                            <Clock size={16} />
                            Being prepared...
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <>
              <div className="client-section-header">
                <h2><User size={24} /> Profile & Documents</h2>
                <p>Manage your personal details and travel documents</p>
              </div>

              <div className="profile-section">
                {/* Account Information Card */}
                <div className="profile-card">
                  <div className="profile-card-header">
                    <h3>Account Information</h3>
                    {!editingAccount ? (
                      <Button onClick={() => { setEditingAccount(true); setAccountError(''); setAccountSuccess(''); }} className="edit-btn" data-testid="edit-account">
                        <Edit size={16} />
                        Edit
                      </Button>
                    ) : (
                      <div className="profile-actions">
                        <Button onClick={saveAccountInfo} disabled={savingAccount} className="save-btn" data-testid="save-account">
                          {savingAccount ? 'Saving...' : 'Save'}
                        </Button>
                        <Button onClick={() => { setEditingAccount(false); setAccountError(''); }} variant="outline" className="cancel-btn">
                          Cancel
                        </Button>
                      </div>
                    )}
                  </div>

                  {accountError && (
                    <div style={{ padding: '0.75rem 1.5rem' }}>
                      <div className="error-banner">
                        <AlertCircle size={16} />
                        <span>{accountError}</span>
                      </div>
                    </div>
                  )}
                  {accountSuccess && (
                    <div style={{ padding: '0.75rem 1.5rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 1rem', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', color: '#166534', fontSize: '0.9rem' }}>
                        <CheckCircle size={16} />
                        <span>{accountSuccess}</span>
                      </div>
                    </div>
                  )}

                  <div className="profile-grid">
                    <div className="profile-field">
                      <label><User size={16} /> First Name</label>
                      {editingAccount ? (
                        <input
                          type="text"
                          value={accountInfo.first_name}
                          onChange={(e) => setAccountInfo({...accountInfo, first_name: e.target.value})}
                          data-testid="account-first-name"
                        />
                      ) : (
                        <span>{accountInfo.first_name || 'Not set'}</span>
                      )}
                    </div>
                    <div className="profile-field">
                      <label><User size={16} /> Last Name</label>
                      {editingAccount ? (
                        <input
                          type="text"
                          value={accountInfo.last_name}
                          onChange={(e) => setAccountInfo({...accountInfo, last_name: e.target.value})}
                          data-testid="account-last-name"
                        />
                      ) : (
                        <span>{accountInfo.last_name || 'Not set'}</span>
                      )}
                    </div>
                    <div className="profile-field">
                      <label><Mail size={16} /> Email</label>
                      {editingAccount ? (
                        <input
                          type="email"
                          value={accountInfo.email}
                          onChange={(e) => setAccountInfo({...accountInfo, email: e.target.value})}
                          data-testid="account-email"
                        />
                      ) : (
                        <span>{accountInfo.email}</span>
                      )}
                    </div>
                    <div className="profile-field">
                      <label><Phone size={16} /> Phone</label>
                      {editingAccount ? (
                        <input
                          type="tel"
                          value={accountInfo.phone}
                          onChange={(e) => setAccountInfo({...accountInfo, phone: e.target.value})}
                          data-testid="account-phone"
                        />
                      ) : (
                        <span>{accountInfo.phone || 'Not set'}</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Change Password Card */}
                <div className="profile-card">
                  <div className="profile-card-header">
                    <h3>Change Password</h3>
                    {!changingPassword && (
                      <Button onClick={() => { setChangingPassword(true); setPasswordError(''); setPasswordSuccess(''); }} className="edit-btn" data-testid="change-password-btn">
                        <Lock size={16} />
                        Change
                      </Button>
                    )}
                  </div>

                  {passwordError && (
                    <div style={{ padding: '0.75rem 1.5rem' }}>
                      <div className="error-banner">
                        <AlertCircle size={16} />
                        <span>{passwordError}</span>
                      </div>
                    </div>
                  )}
                  {passwordSuccess && (
                    <div style={{ padding: '0.75rem 1.5rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 1rem', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', color: '#166534', fontSize: '0.9rem' }}>
                        <CheckCircle size={16} />
                        <span>{passwordSuccess}</span>
                      </div>
                    </div>
                  )}

                  {changingPassword && (
                    <div className="profile-grid" style={{ gridTemplateColumns: '1fr' }}>
                      <div className="profile-field">
                        <label><Lock size={16} /> Current Password</label>
                        <div className="password-field-wrapper">
                          <input
                            type={showCurrentPassword ? 'text' : 'password'}
                            value={passwordData.current_password}
                            onChange={(e) => setPasswordData({...passwordData, current_password: e.target.value})}
                            placeholder="Enter current password"
                            data-testid="current-password-input"
                          />
                          <button
                            type="button"
                            className="password-field-toggle"
                            onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                          >
                            {showCurrentPassword ? <Eye size={16} /> : <EyeOff size={16} />}
                          </button>
                        </div>
                      </div>
                      <div className="profile-field">
                        <label><Lock size={16} /> New Password</label>
                        <div className="password-field-wrapper">
                          <input
                            type={showNewPassword ? 'text' : 'password'}
                            value={passwordData.new_password}
                            onChange={(e) => setPasswordData({...passwordData, new_password: e.target.value})}
                            placeholder="Min 8 characters"
                            data-testid="new-password-input"
                          />
                          <button
                            type="button"
                            className="password-field-toggle"
                            onClick={() => setShowNewPassword(!showNewPassword)}
                          >
                            {showNewPassword ? <Eye size={16} /> : <EyeOff size={16} />}
                          </button>
                        </div>
                      </div>
                      <div className="profile-field">
                        <label><Lock size={16} /> Confirm New Password</label>
                        <input
                          type="password"
                          value={passwordData.confirm_password}
                          onChange={(e) => setPasswordData({...passwordData, confirm_password: e.target.value})}
                          placeholder="Re-enter new password"
                          data-testid="confirm-new-password-input"
                        />
                      </div>
                      <div className="profile-actions" style={{ padding: '0 0 0.5rem', justifyContent: 'flex-start' }}>
                        <Button onClick={savePassword} disabled={savingPassword} className="save-btn" data-testid="save-password">
                          {savingPassword ? 'Saving...' : 'Update Password'}
                        </Button>
                        <Button onClick={() => { setChangingPassword(false); setPasswordError(''); setPasswordData({ current_password: '', new_password: '', confirm_password: '' }); }} variant="outline" className="cancel-btn">
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Personal Information Card */}
                <div className="profile-card">
                  <div className="profile-card-header">
                    <h3>Personal Information</h3>
                    {!editingProfile ? (
                      <Button onClick={() => setEditingProfile(true)} className="edit-btn" data-testid="edit-profile">
                        <Edit size={16} />
                        Edit
                      </Button>
                    ) : (
                      <div className="profile-actions">
                        <Button onClick={saveProfile} disabled={savingProfile} className="save-btn" data-testid="save-profile">
                          {savingProfile ? 'Saving...' : 'Save'}
                        </Button>
                        <Button onClick={() => setEditingProfile(false)} variant="outline" className="cancel-btn">
                          Cancel
                        </Button>
                      </div>
                    )}
                  </div>

                  <div className="profile-grid">
                    <div className="profile-field">
                      <label><Cake size={16} /> Birthday</label>
                      {editingProfile ? (
                        <input
                          type="date"
                          value={profile.birthday}
                          onChange={(e) => setProfile({...profile, birthday: e.target.value})}
                          data-testid="profile-birthday"
                        />
                      ) : (
                        <span>{profile.birthday || 'Not set'}</span>
                      )}
                    </div>

                    <div className="profile-field">
                      <label><Heart size={16} /> Anniversary</label>
                      {editingProfile ? (
                        <input
                          type="date"
                          value={profile.anniversary}
                          onChange={(e) => setProfile({...profile, anniversary: e.target.value})}
                          data-testid="profile-anniversary"
                        />
                      ) : (
                        <span>{profile.anniversary || 'Not set'}</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Travel Documents Card */}
                <div className="profile-card">
                  <div className="profile-card-header">
                    <h3>Travel Documents</h3>
                    {!editingProfile ? (
                      <Button onClick={() => setEditingProfile(true)} className="edit-btn">
                        <Edit size={16} />
                        Edit
                      </Button>
                    ) : null}
                  </div>

                  <div className="profile-grid">
                    <div className="profile-field">
                      <label><CreditCard size={16} /> Aadhar Number</label>
                      {editingProfile ? (
                        <input
                          type="text"
                          value={profile.aadhar_number}
                          onChange={(e) => setProfile({...profile, aadhar_number: e.target.value})}
                          placeholder="XXXX XXXX XXXX"
                          maxLength="14"
                          data-testid="profile-aadhar"
                        />
                      ) : (
                        <span>{profile.aadhar_number ? `****${profile.aadhar_number.slice(-4)}` : 'Not set'}</span>
                      )}
                    </div>

                    <div className="profile-field">
                      <label><CreditCard size={16} /> PAN Number</label>
                      {editingProfile ? (
                        <input
                          type="text"
                          value={profile.pan_number}
                          onChange={(e) => setProfile({...profile, pan_number: e.target.value.toUpperCase()})}
                          placeholder="ABCDE1234F"
                          maxLength="10"
                          data-testid="profile-pan"
                        />
                      ) : (
                        <span>{profile.pan_number ? `${profile.pan_number.slice(0,3)}****${profile.pan_number.slice(-2)}` : 'Not set'}</span>
                      )}
                    </div>

                    <div className="profile-field">
                      <label><FileCheck size={16} /> Passport Number</label>
                      {editingProfile ? (
                        <input
                          type="text"
                          value={profile.passport_number}
                          onChange={(e) => setProfile({...profile, passport_number: e.target.value.toUpperCase()})}
                          placeholder="A1234567"
                          maxLength="10"
                          data-testid="profile-passport"
                        />
                      ) : (
                        <span>{profile.passport_number ? `****${profile.passport_number.slice(-4)}` : 'Not set'}</span>
                      )}
                    </div>

                    <div className="profile-field">
                      <label><Calendar size={16} /> Passport Expiry</label>
                      {editingProfile ? (
                        <input
                          type="date"
                          value={profile.passport_expiry}
                          onChange={(e) => setProfile({...profile, passport_expiry: e.target.value})}
                          data-testid="profile-passport-expiry"
                        />
                      ) : (
                        <span>{profile.passport_expiry || 'Not set'}</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Document Upload Section */}
                <div className="profile-card">
                  <div className="profile-card-header">
                    <h3>Upload Documents</h3>
                    <div className="upload-buttons">
                      <input
                        type="file"
                        ref={fileInputRef}
                        style={{ display: 'none' }}
                        accept=".jpg,.jpeg,.png,.pdf"
                        onChange={(e) => handleFileUpload(e, 'general')}
                      />
                      <select 
                        className="doc-type-select"
                        onChange={(e) => {
                          if (e.target.value) {
                            const input = document.createElement('input');
                            input.type = 'file';
                            input.accept = '.jpg,.jpeg,.png,.pdf';
                            input.onchange = (ev) => handleFileUpload(ev, e.target.value);
                            input.click();
                            e.target.value = '';
                          }
                        }}
                        disabled={uploadingDoc}
                        data-testid="upload-doc-select"
                      >
                        <option value="">+ Upload Document</option>
                        <option value="aadhar">Aadhar Card</option>
                        <option value="pan">PAN Card</option>
                        <option value="passport">Passport</option>
                        <option value="visa">Visa</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                  </div>

                  {uploadMessage && (
                    <div style={{ padding: '0.75rem 1.5rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 1rem', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', color: '#166534', fontSize: '0.9rem' }}>
                        <CheckCircle size={16} />
                        <span>{uploadMessage}</span>
                      </div>
                    </div>
                  )}
                  {uploadError && (
                    <div style={{ padding: '0.75rem 1.5rem' }}>
                      <div className="error-banner">
                        <AlertCircle size={16} />
                        <span>{uploadError}</span>
                      </div>
                    </div>
                  )}

                  <div className="documents-list">
                    {documents.length === 0 ? (
                      <p className="no-docs">No documents uploaded yet. Upload your travel documents for easy access.</p>
                    ) : (
                      documents.map((doc) => (
                        <div key={doc.id} className="document-item" data-testid={`doc-${doc.id}`}>
                          <div className="doc-info">
                            <FileCheck size={20} />
                            <div>
                              <span className="doc-name">{doc.name || 'Document'}</span>
                              <span className="doc-type">{(doc.doc_type || '').charAt(0).toUpperCase() + (doc.doc_type || '').slice(1)}</span>
                            </div>
                          </div>
                          <div className="doc-actions">
                            <a 
                              href={`${BACKEND_URL}${doc.url}`}
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="view-doc-btn"
                              data-testid={`view-doc-${doc.id}`}
                            >
                              <Eye size={16} />
                            </a>
                            <button 
                              onClick={() => deleteDocument(doc.id)}
                              className="delete-doc-btn"
                              data-testid={`delete-doc-${doc.id}`}
                            >
                              <X size={16} />
                            </button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Quick Actions - Fixed at bottom */}
          <div className="client-quick-actions">
            <h3>Need Help?</h3>
            <div className="quick-action-buttons">
              <Button onClick={() => navigate('/design-journey')} className="action-btn" data-testid="quick-action-itinerary">
                Request New Itinerary
              </Button>
              <Button onClick={() => navigate('/contact')} className="action-btn secondary" data-testid="quick-action-contact">
                Contact Us
              </Button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ClientDashboard;
